import { Controller } from '@hotwired/stimulus'
import JSZip from 'jszip'

export default class extends Controller {
  static targets = ['downloadButton', 'attachmentLink', 'fileName']
  static values = {
    filename: String,
    noAttachmentsMessage: { type: String, default: 'No attachments to download' },
    preparingMessage: { type: String, default: 'Preparing download...' },
    processingMessage: { type: String, default: 'Processing %{current}/%{total} files...' },
    generatingMessage: { type: String, default: 'Generating zip file...' },
    creationFailedMessage: { type: String, default: 'Failed to create zip file. Please try again.' }
  }

  connect () {
    this.updateDownloadButton()
    // Re-check when the DOM changes (e.g., new attachments added)
    this.observer = new MutationObserver(() => this.updateDownloadButton())
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect () {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  updateDownloadButton () {
    const hasAttachments = this.attachmentLinkTargets.length > 0
    if (this.hasDownloadButtonTarget) {
      this.downloadButtonTarget.style.display = hasAttachments ? 'inline-block' : 'none'
    }
  }

  async downloadAll () {
    const zip = new JSZip()
    const attachmentLinks = this.attachmentLinkTargets

    if (attachmentLinks.length === 0) {
      alert(this.noAttachmentsMessageValue)
      return
    }

    // Show loading state
    const button = this.downloadButtonTarget
    const originalText = button.textContent
    button.textContent = this.preparingMessageValue
    button.disabled = true

    try {
      // Create an array of promises to fetch all files
      const filePromises = attachmentLinks.map(async (link, index) => {
        const url = link.href
        const filename = link.textContent.trim()

        try {
          const response = await fetch(url, {
            mode: 'cors',
            credentials: 'same-origin'
          })

          if (!response.ok) {
            throw new Error(`Failed to fetch ${filename}: ${response.statusText}`)
          }

          const blob = await response.blob()

          // Add file to zip with a unique name if there are duplicates
          let zipFilename = filename
          let counter = 1
          while (zip.file(zipFilename)) {
            const extension = filename.includes('.') ? filename.substring(filename.lastIndexOf('.')) : ''
            const nameWithoutExt = extension ? filename.substring(0, filename.lastIndexOf('.')) : filename
            zipFilename = `${nameWithoutExt}_${counter}${extension}`
            counter++
          }

          zip.file(zipFilename, blob)

          // Update progress
          button.textContent = this.processingMessageValue
            .replace('%{current}', index + 1)
            .replace('%{total}', attachmentLinks.length)
        } catch (error) {
          console.error(`Error downloading ${filename}:`, error)
          // Add a note about failed files to the zip
          zip.file(`ERROR_${filename}.txt`, `Failed to download: ${error.message}`)
        }
      })

      // Wait for all files to be processed
      await Promise.all(filePromises)

      // Generate zip file
      button.textContent = this.generatingMessageValue
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Create download link
      const downloadUrl = URL.createObjectURL(zipBlob)
      const downloadLink = document.createElement('a')

      // Generate a meaningful filename
      const filename = this.getFilename()
      const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD format
      const zipFilename = `${filename}_${timestamp}.zip`

      downloadLink.href = downloadUrl
      downloadLink.download = zipFilename
      downloadLink.style.display = 'none'

      // Trigger download
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)

      // Clean up
      URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error creating zip file:', error)
      alert(this.creationFailedMessageValue)
    } finally {
      // Reset button state
      button.textContent = originalText
      button.disabled = false
    }
  }

  getFilename () {
    // First priority: explicit filename value
    if (this.hasFilenameValue && this.filenameValue) {
      return this.filenameValue
    }

    // Second priority: fileName target
    if (this.hasFileNameTarget) {
      return this.fileNameTarget.textContent.trim().replace(/[^a-zA-Z0-9-_]/g, '_')
    }

    // Fallback: generic name
    return 'attachments'
  }
}
